/**
 * Generic email utility functions
 */

export interface MailtoParams {
  to?: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  body: string;
}

/**
 * Creates a mailto: URL from structured parameters.
 *
 * Encodes recipients, CC, BCC, subject, and body for use in mailto links.
 * Note: Uses semicolon (;) to separate multiple recipients instead of commas
 * for improved compatibility with email clients like Outlook and Thunderbird.
 * RFC 6068 technically specifies commas, but semicolons are more widely supported.
 */
export function createMailtoLink({
  to = [],
  cc = [],
  bcc = [],
  subject,
  body,
}: MailtoParams): string {
  const parts: string[] = [];

  if (cc.length > 0) {
    parts.push(`cc=${encodeURIComponent(cc.join(";"))}`);
  }

  if (bcc.length > 0) {
    parts.push(`bcc=${encodeURIComponent(bcc.join(";"))}`);
  }

  if (subject) {
    parts.push(`subject=${encodeURIComponent(subject)}`);
  }

  if (body) {
    parts.push(`body=${encodeURIComponent(body)}`);
  }

  const queryString = parts.join("&");
  const toAddresses = to.join(";");

  return `mailto:${toAddresses}${queryString ? `?${queryString}` : ""}`;
}
